/**
 * Geometry Utilities
 * Point, polygon, and spatial math for settlement layout.
 */

import type { Point, Polygon, RotatedRect, Polyline } from './types';

// ============================================================================
// POINT OPERATIONS
// ============================================================================

export function point(x: number, y: number): Point {
  return { x, y };
}

export function add(a: Point, b: Point): Point {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function sub(a: Point, b: Point): Point {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function scale(p: Point, s: number): Point {
  return { x: p.x * s, y: p.y * s };
}

export function length(p: Point): number {
  return Math.sqrt(p.x * p.x + p.y * p.y);
}

export function normalize(p: Point): Point {
  const len = length(p);
  if (len === 0) return { x: 0, y: 0 };
  return { x: p.x / len, y: p.y / len };
}

export function distance(a: Point, b: Point): number {
  return length(sub(b, a));
}

export function lerp(a: Point, b: Point, t: number): Point {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

export function midpoint(a: Point, b: Point): Point {
  return lerp(a, b, 0.5);
}

export function rotate(p: Point, angle: number, origin: Point = { x: 0, y: 0 }): Point {
  const dx = p.x - origin.x;
  const dy = p.y - origin.y;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    x: origin.x + dx * cos - dy * sin,
    y: origin.y + dx * sin + dy * cos,
  };
}

export function angleBetween(from: Point, to: Point): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

/** Point from polar coordinates relative to origin */
export function fromPolar(angle: number, radius: number, origin: Point = { x: 0, y: 0 }): Point {
  return {
    x: origin.x + Math.cos(angle) * radius,
    y: origin.y + Math.sin(angle) * radius,
  };
}

// ============================================================================
// POLYGON OPERATIONS
// ============================================================================

/** Compute centroid of a polygon */
export function centroid(poly: Polygon): Point {
  let cx = 0, cy = 0;
  for (const p of poly) {
    cx += p.x;
    cy += p.y;
  }
  return { x: cx / poly.length, y: cy / poly.length };
}

/** Signed area of polygon (positive = CCW) */
export function signedArea(poly: Polygon): number {
  let area = 0;
  for (let i = 0; i < poly.length; i++) {
    const j = (i + 1) % poly.length;
    area += poly[i].x * poly[j].y;
    area -= poly[j].x * poly[i].y;
  }
  return area / 2;
}

/** Test if point is inside polygon (ray casting) */
export function pointInPolygon(p: Point, poly: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    if (((yi > p.y) !== (yj > p.y)) && (p.x < (xj - xi) * (p.y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}

/** Generate a roughly circular irregular polygon */
export function irregularPolygon(
  center: Point,
  baseRadius: number,
  vertices: number,
  radiusVariance: number, // 0-1, fraction of baseRadius to vary
  startAngle: number,
): Polygon {
  // We need the caller to pass in jittered radii, but for a simpler API
  // we generate evenly spaced angles and let the caller vary radius.
  // This version returns the polygon with uniform radius — callers should
  // use irregularPolygonWithRadii instead for varying radii.
  const points: Point[] = [];
  const angleStep = (Math.PI * 2) / vertices;
  for (let i = 0; i < vertices; i++) {
    const angle = startAngle + i * angleStep;
    const r = baseRadius * (1 + (radiusVariance * Math.sin(angle * 3)));
    points.push(fromPolar(angle, r, center));
  }
  return points;
}

/** Generate irregular polygon with explicit per-vertex radii */
export function irregularPolygonWithRadii(
  center: Point,
  radii: number[],
  startAngle: number,
): Polygon {
  const n = radii.length;
  const angleStep = (Math.PI * 2) / n;
  const points: Point[] = [];
  for (let i = 0; i < n; i++) {
    const angle = startAngle + i * angleStep;
    points.push(fromPolar(angle, radii[i], center));
  }
  return points;
}

/** Compute axis-aligned bounding box of a polygon */
export function boundingBox(poly: Polygon): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of poly) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/** Compute bounding box of multiple polygons */
export function combinedBounds(
  polys: Polygon[],
  extraPoints: Point[] = [],
): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const allPoints = [...extraPoints];
  for (const poly of polys) allPoints.push(...poly);
  for (const p of allPoints) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

// ============================================================================
// ROTATED RECTANGLE OPERATIONS
// ============================================================================

/** Get the 4 corners of a rotated rectangle */
export function rectCorners(rect: RotatedRect): [Point, Point, Point, Point] {
  const hw = rect.width / 2;
  const hh = rect.height / 2;
  const corners: [Point, Point, Point, Point] = [
    { x: -hw, y: -hh },
    { x: hw, y: -hh },
    { x: hw, y: hh },
    { x: -hw, y: hh },
  ];
  return corners.map(c => rotate(add(c, rect.center), rect.rotation, rect.center)) as [Point, Point, Point, Point];
}

/** Check if two rotated rectangles overlap (SAT — Separating Axis Theorem) */
export function rectsOverlap(a: RotatedRect, b: RotatedRect, padding = 0): boolean {
  const cornersA = rectCorners(padRect(a, padding));
  const cornersB = rectCorners(b);
  return satOverlap(cornersA, cornersB);
}

function padRect(rect: RotatedRect, padding: number): RotatedRect {
  return { ...rect, width: rect.width + padding * 2, height: rect.height + padding * 2 };
}

function satOverlap(polyA: Point[], polyB: Point[]): boolean {
  const axes = [...getAxes(polyA), ...getAxes(polyB)];
  for (const axis of axes) {
    const [minA, maxA] = project(polyA, axis);
    const [minB, maxB] = project(polyB, axis);
    if (maxA < minB || maxB < minA) return false;
  }
  return true;
}

function getAxes(poly: Point[]): Point[] {
  const axes: Point[] = [];
  for (let i = 0; i < poly.length; i++) {
    const j = (i + 1) % poly.length;
    const edge = sub(poly[j], poly[i]);
    axes.push(normalize({ x: -edge.y, y: edge.x })); // perpendicular
  }
  return axes;
}

function project(poly: Point[], axis: Point): [number, number] {
  let min = Infinity, max = -Infinity;
  for (const p of poly) {
    const dot = p.x * axis.x + p.y * axis.y;
    if (dot < min) min = dot;
    if (dot > max) max = dot;
  }
  return [min, max];
}

/** Check if a rotated rectangle is fully inside a polygon */
export function rectInsidePolygon(rect: RotatedRect, poly: Polygon): boolean {
  const corners = rectCorners(rect);
  return corners.every(c => pointInPolygon(c, poly));
}

// ============================================================================
// POLYLINE / CURVE UTILITIES
// ============================================================================

/** Smooth a polyline with Chaikin's corner-cutting algorithm */
export function smoothPolyline(points: Polyline, iterations = 2): Polyline {
  let current = points;
  for (let iter = 0; iter < iterations; iter++) {
    if (current.length < 2) return current;
    const next: Polyline = [current[0]]; // keep first point
    for (let i = 0; i < current.length - 1; i++) {
      const a = current[i];
      const b = current[i + 1];
      next.push(lerp(a, b, 0.25));
      next.push(lerp(a, b, 0.75));
    }
    next.push(current[current.length - 1]); // keep last point
    current = next;
  }
  return current;
}

/** Compute total length of a polyline */
export function polylineLength(points: Polyline): number {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += distance(points[i - 1], points[i]);
  }
  return total;
}

/** Compute convex hull of a set of points (Graham scan) */
export function convexHull(points: Point[]): Polygon {
  if (points.length < 3) return [...points];

  // Find lowest-y point (leftmost if tie)
  let pivot = 0;
  for (let i = 1; i < points.length; i++) {
    if (points[i].y < points[pivot].y || (points[i].y === points[pivot].y && points[i].x < points[pivot].x)) {
      pivot = i;
    }
  }
  const pivotPoint = points[pivot];

  // Sort by polar angle relative to pivot
  const sorted = points
    .filter((_, i) => i !== pivot)
    .sort((a, b) => {
      const angleA = Math.atan2(a.y - pivotPoint.y, a.x - pivotPoint.x);
      const angleB = Math.atan2(b.y - pivotPoint.y, b.x - pivotPoint.x);
      if (angleA !== angleB) return angleA - angleB;
      return distance(pivotPoint, a) - distance(pivotPoint, b);
    });

  const hull: Point[] = [pivotPoint];
  for (const p of sorted) {
    while (hull.length >= 2 && cross(hull[hull.length - 2], hull[hull.length - 1], p) <= 0) {
      hull.pop();
    }
    hull.push(p);
  }
  return hull;
}

function cross(o: Point, a: Point, b: Point): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

/** Expand a polygon outward by a fixed distance */
export function offsetPolygon(poly: Polygon, dist: number): Polygon {
  const c = centroid(poly);
  return poly.map(p => {
    const dir = normalize(sub(p, c));
    return add(p, scale(dir, dist));
  });
}

/** Shrink a polygon inward */
export function insetPolygon(poly: Polygon, dist: number): Polygon {
  return offsetPolygon(poly, -dist);
}
